import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ChefHat, Bath, Layers, Building2, Home as HomeIcon, TreePine, Star, Quote,
} from "lucide-react";
import { CTABand } from "../components/Layout";
import { Seo } from "../components/Seo";
import { IMAGES, SERVICES, TESTIMONIALS } from "../data";

const ICONS = { ChefHat, Bath, Layers, Building2, Home: HomeIcon, TreePine };

const Home = () => {
  const featured = SERVICES.slice(0, 3);

  return (
    <div data-testid="home-page">
      <Seo
        title="Bright Oak Renovations | Renovation, Rooted in Craft"
        description="Premium home renovations & general contracting in Cambridge, Kitchener, Waterloo & Guelph, Ontario. Kitchens, bathrooms, basements, additions & more."
        path="/"
      />
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Modern renovated kitchen" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2B2B2B]/85 via-[#2B2B2B]/55 to-[#2B2B2B]/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full py-24">
          <div className="max-w-2xl">
            <p className="section-label text-[#C68B4E] animate-fade-up">Cambridge · Kitchener · Waterloo · Guelph</p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#F7F3EC] mt-5 leading-[1.05] animate-fade-up"
              style={{ animationDelay: "0.1s" }}
            >
              Renovation,<br />Rooted in Craft
            </h1>
            <p
              className="text-[#F7F3EC]/85 text-base md:text-lg mt-6 max-w-xl leading-relaxed animate-fade-up"
              style={{ animationDelay: "0.2s" }}
            >
              Bright Oak Renovations is a full-service general contractor building beautiful,
              enduring homes across Waterloo Region — with the honesty and craftsmanship your
              home deserves.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10 animate-fade-up" style={{ animationDelay: "0.3s" }}>
              <Link
                to="/contact"
                data-testid="hero-quote-button"
                className="bg-[#C68B4E] text-white hover:bg-[#B37A3D] transition-colors px-9 py-4 text-sm font-bold tracking-wider uppercase inline-flex items-center justify-center gap-2 group"
              >
                Get a Free Quote
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/gallery"
                data-testid="hero-gallery-button"
                className="border-2 border-[#F7F3EC] text-[#F7F3EC] hover:bg-[#F7F3EC] hover:text-[#2B2B2B] transition-colors px-9 py-4 text-sm font-bold tracking-wider uppercase text-center"
              >
                View Our Work
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO / STATS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28" data-testid="intro-section">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="section-label">Who We Are</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-4 leading-tight">
              A local team that treats your home like our own.
            </h2>
            <p className="text-[#5C5C5C] text-base md:text-lg mt-6 leading-relaxed">
              For over fifteen years, Bright Oak has helped Cambridge-area families reimagine the
              spaces they live in. From a single bathroom to a full-home transformation, we bring
              disciplined project management, master craftsmanship, and a genuinely personal touch
              to every build.
            </p>
            <div className="grid grid-cols-3 gap-6 mt-10">
              {[
                { n: "15+", l: "Years" },
                { n: "400+", l: "Projects" },
                { n: "100%", l: "Referral-driven" },
              ].map((s) => (
                <div key={s.l} className="border-l-2 border-[#C68B4E] pl-4">
                  <div className="text-3xl font-extrabold text-[#2B2B2B]">{s.n}</div>
                  <div className="text-xs font-bold uppercase tracking-widest text-[#7A8B6F] mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src={IMAGES.team} alt="Bright Oak carpenter crafting custom woodwork" className="w-full h-64 object-cover mt-8" />
            <img src={IMAGES.bathroom} alt="Renovated modern bathroom with elegant tile and lighting" className="w-full h-64 object-cover" />
          </div>
        </div>
      </section>

      {/* SERVICES HIGHLIGHT */}
      <section className="bg-white border-y border-[#2B2B2B]/10 py-20 lg:py-28" data-testid="services-highlight">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <p className="section-label">What We Do</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-4">Our Renovation Services</h2>
            </div>
            <Link to="/services" className="text-sm font-bold uppercase tracking-wider text-[#C68B4E] inline-flex items-center gap-2 hover:gap-3 transition-all" data-testid="view-all-services-link">
              View All Services <ArrowRight size={18} />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {featured.map((s) => {
              const Icon = ICONS[s.icon];
              return (
                <Link
                  to="/services"
                  key={s.slug}
                  data-testid={`featured-service-${s.slug}`}
                  className="group bg-[#F7F3EC] border border-[#2B2B2B]/10 hover:shadow-xl transition-shadow overflow-hidden"
                >
                  <div className="h-52 overflow-hidden">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-7">
                    <Icon size={28} strokeWidth={1.5} className="text-[#C68B4E]" />
                    <h3 className="text-xl font-bold mt-4">{s.title}</h3>
                    <p className="text-sm text-[#5C5C5C] mt-2 leading-relaxed">{s.short}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28" data-testid="featured-projects">
        <p className="section-label">Featured Work</p>
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-4 mb-12">Recent Transformations</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:h-[520px]">
          <div className="md:row-span-2 relative group overflow-hidden">
            <img src={IMAGES.wholeHome} alt="Whole home renovation" className="w-full h-full object-cover min-h-[260px] group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2B2B2B]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
              <span className="text-white font-bold">Open-Concept Living · Waterloo</span>
            </div>
          </div>
          <div className="relative group overflow-hidden">
            <img src={IMAGES.kitchen} alt="Kitchen renovation" className="w-full h-full object-cover min-h-[180px] group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2B2B2B]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
              <span className="text-white font-bold">Modern Kitchen · Cambridge</span>
            </div>
          </div>
          <div className="relative group overflow-hidden">
            <img src={IMAGES.exterior} alt="Deck build" className="w-full h-full object-cover min-h-[180px] group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2B2B2B]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
              <span className="text-white font-bold">Cedar Deck · Cambridge</span>
            </div>
          </div>
        </div>
        <div className="mt-10">
          <Link to="/gallery" className="border-2 border-[#2B2B2B] text-[#2B2B2B] hover:bg-[#2B2B2B] hover:text-[#F7F3EC] transition-colors px-9 py-4 text-sm font-bold tracking-wider uppercase inline-block" data-testid="home-gallery-link">
            Explore Full Gallery
          </Link>
        </div>
      </section>

      {/* TESTIMONIALS PREVIEW */}
      <section className="bg-[#2B2B2B] text-[#F7F3EC] py-20 lg:py-28" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="section-label">Client Stories</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-4 mb-14">Homeowners Who Trust Bright Oak</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} data-testid={`testimonial-${i}`} className="bg-[#F7F3EC]/[0.04] border-l-4 border-[#C68B4E] p-8">
                <Quote size={30} className="text-[#C68B4E]" />
                <div className="flex gap-1 mt-4">
                  {[...Array(5)].map((_, k) => (
                    <Star key={k} size={16} className="fill-[#C68B4E] text-[#C68B4E]" />
                  ))}
                </div>
                <p className="text-[#F7F3EC]/85 mt-5 leading-relaxed text-sm">"{t.quote}"</p>
                <div className="flex items-center gap-3 mt-6">
                  <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
                  <div>
                    <div className="font-bold text-sm">{t.name}</div>
                    <div className="text-xs text-[#C68B4E]">{t.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
};

export default Home;
